import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MapPin, Building2, Pencil } from "lucide-react";
import CompletionBadge from "./CompletionBadge";
import { useProfileCompletion, type ProfileRow } from "@/hooks/useProfileCompletion";

interface ProfileHeaderProps {
  displayName: string;
  email?: string;
  avatarUrl?: string | null;
  bio?: string | null;
  organization?: string | null;
  role?: string | null;
  location?: string | null;
  ideaCount: number;
  supportingCount: number;
  commentCount?: number | null;
  savedCount?: number | null;
  /** When true, renders an "Edit profile" button linking to /profile. */
  showEditButton?: boolean;
  /** Optional full profile row used to derive the completion badge. */
  profile?: ProfileRow | null;
}

const ProfileHeader = ({
  displayName,
  email,
  avatarUrl,
  bio,
  organization,
  role,
  location,
  ideaCount,
  supportingCount,
  commentCount,
  savedCount,
  showEditButton,
  profile,
}: ProfileHeaderProps) => {
  const { tier } = useProfileCompletion(profile ?? null);
  const initials = (displayName || email || "?")
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");

  return (
    <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
      <div className="flex flex-col gap-6 md:flex-row md:items-start">
        <Avatar className="h-24 w-24 border border-border md:h-28 md:w-28">
          {avatarUrl ? <AvatarImage src={avatarUrl} alt={displayName} /> : null}
          <AvatarFallback className="bg-muted text-2xl font-semibold text-foreground">
            {initials || "U"}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl font-bold text-foreground md:text-3xl">
                  {displayName || "Shareholder"}
                </h1>
                {profile ? <CompletionBadge tier={tier} hideOnZero /> : null}
              </div>
              {profile?.headline ? (
                <p className="text-sm text-muted-foreground">{profile.headline}</p>
              ) : null}
              {email ? <p className="text-sm text-muted-foreground">{email}</p> : null}
            </div>
            {showEditButton ? (
              <Link to="/profile">
                <Button variant="outline" size="sm">
                  <Pencil className="mr-2 h-3.5 w-3.5" />
                  Edit profile
                </Button>
              </Link>
            ) : null}
          </div>

          {role ? <Badge variant="secondary">{role}</Badge> : null}

          {bio ? (
            <p className="text-sm leading-relaxed text-foreground/90">{bio}</p>
          ) : (
            <p className="text-sm italic text-muted-foreground">
              {showEditButton
                ? "Add a bio in Settings to introduce yourself to the community."
                : "This shareholder hasn't added a bio yet."}
            </p>
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
            {organization ? (
              <span className="inline-flex items-center gap-1.5">
                <Building2 className="h-3.5 w-3.5" />
                {organization}
              </span>
            ) : null}
            {location ? (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {location}
              </span>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-6 pt-2">
            <div>
              <div className="font-display text-xl font-bold text-foreground">{ideaCount}</div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Projects</div>
            </div>
            <div>
              <div className="font-display text-xl font-bold text-foreground">{supportingCount}</div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Supporting</div>
            </div>
            {typeof commentCount === "number" ? (
              <div>
                <div className="font-display text-xl font-bold text-foreground">{commentCount}</div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Comments</div>
              </div>
            ) : null}
            {typeof savedCount === "number" ? (
              <div>
                <div className="font-display text-xl font-bold text-foreground">{savedCount}</div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Saved</div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;
